import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileTabBar } from './MobileTabBar';
import { useUIStore } from '../../store';
import { BookmarkProvider } from '../../hooks/useBookmarks';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { AccessExpired } from '../common/AccessExpired';
import { getBillingStatus } from '../../services/billing.api';
import { useAuth } from '../../auth/useAuth';

/**
 * Main application shell layout.
 * Fixed left sidebar, top header bar, and scrollable content area.
 * Also enforces the onboarding gate: self-registered users who haven't
 * completed the Stripe checkout are redirected to /onboarding.
 */
export const AppShell: React.FC = () => {
  const { isSidebarOpen, isSidebarCollapsed } = useUIStore();
  const { user } = useAuth();
  const location = useLocation();

  // Any API call refused for an expired access window takes over the content
  // area. Without this every tab just showed its own generic "could not load".
  const [expiredAt, setExpiredAt] = React.useState<string | null | undefined>(undefined);
  React.useEffect(() => {
    const onExpired = (e: Event) => {
      const detail = (e as CustomEvent<{ expiredAt?: string }>).detail;
      setExpiredAt(detail?.expiredAt ?? null);
    };
    window.addEventListener('stratmined:access-expired', onExpired);
    return () => window.removeEventListener('stratmined:access-expired', onExpired);
  }, []);

  // Only external users can require onboarding — skip the check for staff.
  const shouldCheck = user?.role === 'external';
  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: getBillingStatus,
    enabled: shouldCheck,
    staleTime: 30_000,
  });

  if (shouldCheck && billingLoading) {
    return <LoadingSpinner message="Loading your workspace…" />;
  }
  if (shouldCheck && billing?.requiresOnboarding && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <BookmarkProvider>
    <div className="sm-print-flow flex h-screen overflow-hidden bg-[#F8FAFC]">
      {/* Sidebar */}
      <Sidebar />

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 lg:hidden"
          onClick={() => useUIStore.getState().setSidebarOpen(false)}
        />
      )}

      {/* Main content area */}
      <div
        className={`sm-print-root flex flex-1 flex-col min-w-0 transition-all duration-200 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        {/* Header */}
        <Header />

        {/* Page content */}
        <main className="sm-print-flow flex-1 overflow-y-auto px-7 py-5 pb-[82px] lg:pb-5">
          <div className="max-w-[1600px] animate-fade-in">
            <ErrorBoundary>
              {expiredAt !== undefined ? (
                <AccessExpired expiredAt={expiredAt ?? undefined} />
              ) : (
                <Outlet />
              )}
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* Thumb-reachable navigation on small screens. */}
      <MobileTabBar />
    </div>
    </BookmarkProvider>
  );
};
